/**
 * SOLID Review
 * S — Strong. One concern: LiveKit's signaling and admin reads on the server, as the `ClientMediaServer`
 *     port speaks them.
 * O — Good. A new media call is one method over the same adapter.
 * L — Strong. Implements the port interface exactly.
 * I — Good. The port's five ops — the whole media GraphQL surface.
 * D — Strong. The Application's adapter interface and the shared Result.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { PlayerId, RoomId } from '../../application/ports/ClientVocabulary';
import type {
  IPayloadMediaToken, IPayloadMediaConnection, IPayloadMediaPresence, IPayloadMediaHealth, IPayloadMediaUsage,
  IEntityMediaToken, IEntityMediaHealth, IEntityMediaUsage,
} from '../../domain/ports/schemas/MediaMoves';
import type { IPortClientMediaServer } from '../../application/ports/contracts';
import type {
  IAdapterApolloLiveKit, IEntityLiveKitToken, IEntityLiveKitHealth, IEntityLiveKitUsageReport,
} from '../../application/livekit/ports/IAdapterApolloLiveKit';
import { ok } from '../../shared/Result';

/**
 * THE MEDIA GATEWAY (frontend strict-ports plan, FE5b, 2026-10-04), over `ApolloLiveKitAdapter` — the
 * Framework's `LiveKitAdapter` socket until now, called by useLiveKitSession, ControlPanel's admin line and
 * the usage report. The calls are unchanged (the token cached per room and player until it expires); the
 * answers are the fields the screens read. The adapter's failure is the port's `{ code, message }` since
 * frontend-infrastructure-oo P3 (§28.288): it passes through.
 */
export class ClientMediaServerAdapter implements IPortClientMediaServer {
  constructor(private readonly media: IAdapterApolloLiveKit) {}

  async token(_ctx: IContextRequest, request: IPayloadMediaToken) {
    const token = await this.media.getLiveKitToken(request.roomId as RoomId, request.playerId as PlayerId);
    return token.success ? ok(this.tokenOf(token.value)) : token;
  }

  async reportConnection(_ctx: IContextRequest, request: IPayloadMediaConnection) {
    const reported = await this.media.reportLiveKitConnectionState(request.roomId as RoomId, request.playerId as PlayerId, request.connected);
    return reported.success ? ok(undefined) : reported;
  }

  async markPresence(_ctx: IContextRequest, request: IPayloadMediaPresence) {
    const marked = await this.media.markMediaPresence(request.roomId as RoomId, request.usedVideo, request.usedAudio);
    return marked.success ? ok(undefined) : marked;
  }

  async health(_ctx: IContextRequest, request: IPayloadMediaHealth) {
    const health = await this.media.getLiveKitHealth(request.playerId as PlayerId);
    return health.success ? ok(this.healthOf(health.value)) : health;
  }

  async usageReport(_ctx: IContextRequest, request: IPayloadMediaUsage) {
    const report = await this.media.getLiveKitUsageReport(request.playerId as PlayerId, request.startDate, request.endDate);
    return report.success ? ok(this.usageOf(report.value)) : report;
  }

  private tokenOf(token: IEntityLiveKitToken): IEntityMediaToken {
    return { token: token.token, serverUrl: token.serverUrl };
  }

  private healthOf(health: IEntityLiveKitHealth): IEntityMediaHealth {
    return { configured: health.configured, activeRooms: health.activeRooms, activeParticipants: health.activeParticipants };
  }

  private usageOf(report: IEntityLiveKitUsageReport): IEntityMediaUsage {
    return {
      totalMinutes: report.totalMinutes,
      entries: report.entries.map((e) => ({ date: e.date, playerId: e.playerId, minutes: e.minutes })),
    };
  }
}
